import React, { useEffect, useState, useRef } from "react";
import { Button, Container, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useCargoSearchList } from "../../../apis/cargoSearchList/CargoSearchListProvider";
import { CargoSearchListApiResponse } from "../../../services/cargoSearchListService";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";

const ViewCargoSearchListById: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { fetchCargoSearchListById, putCargoSearchList, excluiCargoSearchList } = useCargoSearchList();
    const [cargo, setCargo] = useState<CargoSearchListApiResponse | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const descriptionRef = useRef<HTMLInputElement | null>(null);

    const navItems: NavBarItem[] = [
        { label: t('cargoSearchList.labels.home'), route: "/", iconName: "house-door" },
        { label: t('cargoSearchList.labels.list'), route: "/CargoSearchList", iconName: "list" },
        { label: t('cargoSearchList.labels.new'), route: "/CargoSearchList/create", iconName: "plus-circle" },
    ];

    const types = ["", "Text", "Number", "DateTime"];

    useEffect(() => {
        if (isEditing && descriptionRef.current) {
            descriptionRef.current.focus();
        }
    }, [isEditing]);

    useEffect(() => {
        const loadCargo = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error("ID inválido");

                const data = await fetchCargoSearchListById(Number(id));
                setCargo(data);
            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadCargo();
    }, [id, fetchCargoSearchListById]);

    const handleEdit = async () => {
        if (isEditing) {
            try {
                if (cargo) {
                    await putCargoSearchList(cargo.data.id, cargo.data);

                    const successMessage = getSuccessMessage("editCargoSearchList", { id: cargo.data.id, });
                    navigate(`/CargoSearchList`, {
                        state: {
                            message: successMessage,
                            variant: "success",
                        },
                    });
                }
            } catch (error) {
                alert("Erro ao atualizar os dados.");
            }
        }
        setIsEditing(!isEditing);
    };

    const handleDelete = async () => {
        if (!id) return;
        try {
            await excluiCargoSearchList(Number(id));

            const successMessage = getSuccessMessage("deleteCargoSearchList", { id: id });
            navigate(`/CargoSearchList`, {
                state: {
                    message: successMessage,
                    variant: "success",
                },
            });
        } catch (error) {
            alert("Erro ao excluir o registro.");
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (cargo) {
            setCargo({
                ...cargo,
                data: {
                    ...cargo.data,
                    [e.target.name]: e.target.value,
                },
            });
        }
    };

    if (loading) {
        return <div className="text-center mt-4">Carregando...</div>;
    }

    if (error || !cargo) {
        return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;
    }

    return (
        <Container fluid>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editCargoSearchList") : getPageName("viewCargoSearchList")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Form.Group className="mb-2">
                    <Form.Label>{t('cargoSearchList.fields.id')}</Form.Label>
                    <Form.Control size="sm" readOnly value={cargo.data.id} style={{ width: "20%", backgroundColor: "#f0f0f0" }} />
                </Form.Group>

                <Form.Group className="mb-2">
                    <Form.Label>{t('cargoSearchList.fields.description')}</Form.Label>
                    <Form.Control
                        size="sm"
                        name="description"
                        value={cargo.data.description || ""}
                        onChange={handleChange}
                        readOnly={!isEditing}
                        ref={descriptionRef}
                        style={{ width: "20%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                    />
                </Form.Group>

                <Form.Group className="mb-2">
                    <Form.Label>{t('cargoSearchList.fields.key')}</Form.Label>
                    <Form.Control size="sm" name="key" value={cargo?.data.key} style={{ width: "20%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                        onChange={handleChange}
                        readOnly={!isEditing}

                    />
                </Form.Group>

                {/* <Form.Group className="mb-2">
                    <Form.Label>Type</Form.Label>
                    <Form.Control
                        size="sm"
                        as="select"
                        name="tipoInput"
                        value={cargo.data.tipoInput}
                        disabled={!isEditing}
                        style={{ width: "20%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                        onChange={handleChange}
                    >
                        {types.map((type, index) => (
                            <option key={index} value={type}>
                                {type || "Select a type"}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group> */}
            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewCargoSearchListById;
